import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WindowRef } from 'src/app/util/window.ref';
import { APIResponse } from '../../models/LoginInfoResponse';
import { DeliveryChannelPreferences } from '../../models/MFAPreferencesResponse';

/**
 * refactor [high:8:20230317] move everything in shared service into a proper home.
 */
@Injectable({
  providedIn: 'root'
})
export class SharedService implements OnDestroy {
  private editScreenFlagData = new BehaviorSubject<boolean>(false);
  editScreenFlag$ = this.editScreenFlagData.asObservable();

  private mfaPreferenceChannels = new BehaviorSubject<DeliveryChannelPreferences[]>(null);

  private responseSource = new BehaviorSubject<APIResponse>(null);
  //userInfoResponse$ = this.responseSource.asObservable();


  private roleResponseSource = new BehaviorSubject<APIResponse>(null);
  roleAndStatusResponse$ = this.roleResponseSource.asObservable();

  private destroy = new Subject<void>();

  private countryPhoneCode = new BehaviorSubject<string>(null);

  //phoneCode$ = this.countryPhoneCode.asObservable();

  get phoneCode$(): Observable<string> {
    return this.countryPhoneCode.asObservable().pipe(takeUntil(this.destroy));
  }

  public selectedRoute = new BehaviorSubject<string>('contact');
  selctedRoute$ = this.selectedRoute.asObservable();

  private fdmFlag = new BehaviorSubject<boolean>(false);
  public selectedExternalRoute = new BehaviorSubject<any>('');
  selectedExternalRoute$ = this.selectedExternalRoute.asObservable();
  private editContactScreenData = new BehaviorSubject<boolean>(false);
  editContactScreenData$ = this.editContactScreenData.asObservable();
  public selectedLoginRoute = new BehaviorSubject<boolean>(false);
  selectedLoginRoute$ = this.selectedLoginRoute.asObservable();

  isModalPopupOpen = false;
  isProfileLocked = false;
  fdmFlagError = false;
  contactEditMode = false;
  //isEditLinkClicked = false;
  public mainEvent: EventEmitter<boolean> = new EventEmitter();
  public errorMainEvent: EventEmitter<boolean> = new EventEmitter();
  private isGuardCache: { use: boolean };
  private isEditMode: { use: boolean };

  constructor() { }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  get userInfoResponse$(): Observable<APIResponse> {
    return this.responseSource.asObservable().pipe(takeUntil(this.destroy));
  }

  get mfaChannelsList(): DeliveryChannelPreferences[] {
    return this.mfaPreferenceChannels.getValue();
  }

  set mfaChannelsList(channels: DeliveryChannelPreferences[]) {
    this.mfaPreferenceChannels.next(channels);
  }

  set isEditScreen(flag: boolean) {
    this.editScreenFlagData.next(flag);
  }

  get isEditScreen(): boolean {
    return this.editScreenFlagData.getValue();
  }

  set isEditContactScreen(flag: boolean) {
    this.editContactScreenData.next(flag);
  }

  get isEditContactScreen(): boolean {
    return this.editContactScreenData.getValue();
  }

  // Method to set response
  set apiResponse(apiResponse: APIResponse) {
    this.responseSource.next(apiResponse);
  }

  // Returns an apiResponse from response source
  get apiResponse(): APIResponse {
    return this.responseSource.getValue();
  }

  // Method to set role and status response
  set apiRoleResponse(apiRoleResponse: APIResponse) {
    this.roleResponseSource.next(apiRoleResponse);
  }

  // Returns an apiRoleResponse from response source
  get apiRoleResponse(): APIResponse {
    return this.roleResponseSource.getValue();
  }

  set phoneCode(code: string) {
    this.countryPhoneCode.next(code);
  }

  get phoneCode(): string {
    return this.countryPhoneCode.getValue();
  }

  setSelectedRoute(route: any): void {
    this.selectedRoute.next(route);
  }

  set showFDM(flag: boolean) {
    this.fdmFlag.next(flag);
  }

  get showFDM(): boolean {
    return this.fdmFlag.getValue();
  }

  set isLoginMenuClicked(flag: boolean) {
    this.selectedLoginRoute.next(flag);
  }

  get isLoginMenuClicked(): boolean {
    return this.selectedLoginRoute.getValue();
  }

  openURL(URL: string): void {
    WindowRef.nativeWindow.open(URL, '_self');
  }

  openURLInNewTab(URL: string): void {
    WindowRef.nativeWindow.open(URL, '_blank');
  }

  /**
   * Get the value of a query string.
   *
   * The query param is pulled form the location.href because ActivatedRoute doesn't solve the
   * params and query params until AFTER the component is rendered so they are not available during the interceptor chain.
   *
   * @param name - value key
   * @param url - to pull the value from (optional)
   */
  findQueryParam(name: string, url?: string): string | null {
    if (!name) return null;

    const href = url || WindowRef.nativeWindow.location.href;
    const paramRegex = new RegExp('[?&]' + name + '=([^&#]*)', 'i');
    const param = paramRegex.exec(href);
    return param ? param[1] : null;
  }

  /**
   * Converts a value to boolean and handles string properties: on, yes, true, no, off, and false.
   *
   * @param candidate - to be converted to boolean
   * @param defaultIfMissing - if the candidate is null or undefined
   * @return true if the candidate is truthy, false otherwise
   */
  toBoolean(candidate: string | number | boolean | null, defaultIfMissing = false): boolean {
    if (candidate == null) return defaultIfMissing;
    if (typeof candidate === 'boolean') return candidate;
    if (typeof candidate === 'number') return candidate !== 0;

    const lowCandidate = candidate.toLowerCase();

    if ('on' === lowCandidate || 'yes' === lowCandidate || 'true' === lowCandidate) return true;
    if ('no' === lowCandidate || 'off' === lowCandidate || 'false' === lowCandidate) return false;
    return false;
  }

  isPerformanceExecutorOn(): boolean {
    const useGuard = this.findQueryParam('performance_executor');
    this.isGuardCache = { use: this.toBoolean(useGuard) };

    return this.isGuardCache.use;
  }

  isEditModeOn(): boolean {
    const isEditOn = this.findQueryParam('edit_mode');
    this.isEditMode = { use: this.toBoolean(isEditOn) };
    return this.isEditMode.use;
  }

   navigateExternalLink(url: any): void {
    this.selectedExternalRoute.next(url);
  }
}
